import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ShieldCheck, AlertTriangle } from "lucide-react";

export default function LoginPage() {
  const { login, register, error, setError } = useAuth();
  const [loading, setLoading] = useState(false);
  const [li, setLi] = useState({ email: "", password: "" });
  const [ri, setRi] = useState({ email: "", password: "", name: "", agency: "", address_state: "" });
  // If the user landed here because another device stole their session, surface
  // that reason clearly so they understand why they were signed out. The
  // marker is set by our axios 401 interceptor (see /src/lib/api.js).
  const kickedReason = (() => {
    try {
      if (typeof window !== "undefined" && new URLSearchParams(window.location.search).get("kicked") === "1") {
        return sessionStorage.getItem("psa.kicked_reason") || "session_replaced";
      }
    } catch { /* noop */ }
    return null;
  })();
  useEffect(() => {
    // Clear the marker so a plain refresh of /login doesn't keep showing it.
    if (kickedReason) { try { sessionStorage.removeItem("psa.kicked_reason"); } catch { /* noop */ } }
  }, [kickedReason]);

  const onLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    await login(li.email, li.password);
    setLoading(false);
  };

  const onRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    await register(ri);
    setLoading(false);
  };

  return (
    <div
      data-testid="login-page"
      className="fixed inset-0 grid lg:grid-cols-2 bg-background text-foreground overflow-hidden"
    >
      {/* Left - tactical visual */}
      <div className="hidden lg:flex relative overflow-hidden grid-bg border-r border-border">
        <div className="scanlines absolute inset-0 pointer-events-none" />
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1584931423298-c576fda54bd2?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600)",
            backgroundSize: "cover",
            backgroundPosition: "center",
            mixBlendMode: "screen",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />

        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-3">
            <img src="/branding/psa-logo.png" alt="Public Safety Alerts" className="w-14 h-14 object-contain" />
            <div>
              <div className="font-bold tracking-[0.25em]">PUBLIC SAFETY ALERTS</div>
              <div className="text-[10px] font-mono text-muted-foreground tracking-widest">
                TRUSTED ALERTS FOR PUBLIC SAFETY
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="font-mono text-xs text-muted-foreground">// MISSION_CRITICAL_OPS</div>
            <h2 className="text-4xl font-bold leading-tight">
              Coordinate every call,<br />every unit, every minute.
            </h2>
            <p className="text-sm text-muted-foreground max-w-md">
              Unified CAD console for dispatch, paging, weather, traffic intel and incident command —
              built for the people who answer when the worst happens.
            </p>

            <div className="grid grid-cols-3 gap-3 pt-6 font-mono text-xs">
              {[
                ["P25 SECURE", "ONLINE"],
                ["NWS FEED", "LIVE"],
                ["511 CAMS", "ACTIVE"],
              ].map(([k, v]) => (
                <div key={k} className="border border-border bg-card/60 backdrop-blur-sm p-3 rounded-sm">
                  <div className="text-muted-foreground text-[10px]">{k}</div>
                  <div className="text-green-500 mt-1">● {v}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="font-mono text-[10px] text-muted-foreground tracking-widest">
            SECURE • CJIS-COMPLIANT • AES-256 • SESSION TOKEN HS256
          </div>
        </div>
      </div>

      {/* Right - form */}
      <div className="flex items-center justify-center p-8 relative">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img src="/branding/psa-logo.png" alt="Public Safety Alerts" className="w-12 h-12 object-contain" />
            <div>
              <div className="font-bold tracking-[0.25em]">PUBLIC SAFETY ALERTS</div>
            </div>
          </div>

          <Card className="border-border">
            <CardHeader>
              <CardTitle className="text-2xl">Access Console</CardTitle>
              <CardDescription className="font-mono text-xs tracking-wider">
                AUTHENTICATE TO RESUME DISPATCH OPERATIONS
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="login">
                <TabsList className="grid grid-cols-2 mb-6">
                  <TabsTrigger value="login" data-testid="tab-login">Sign In</TabsTrigger>
                  <TabsTrigger value="register" data-testid="tab-register">Register</TabsTrigger>
                </TabsList>

                <TabsContent value="login">
                  <form onSubmit={onLogin} className="space-y-4">
                    {kickedReason === "session_replaced" && (
                      <Alert data-testid="kicked-banner" className="border-amber-500/50 bg-amber-500/10">
                        <AlertTriangle className="h-4 w-4 text-amber-500" />
                        <AlertDescription className="text-amber-500">
                          You were signed out because this account signed in on another device.
                        </AlertDescription>
                      </Alert>
                    )}
                    <div>
                      <Label htmlFor="login-email">Dispatcher ID</Label>
                      <Input
                        id="login-email"
                        data-testid="login-email-input"
                        type="text"
                        autoComplete="username"
                        placeholder="e.g. 1001NJ"
                        value={li.email}
                        onChange={(e) => setLi({ ...li, email: e.target.value })}
                        className="font-mono uppercase"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="login-password">Passcode</Label>
                      <Input
                        id="login-password"
                        data-testid="login-password-input"
                        type="password"
                        value={li.password}
                        onChange={(e) => setLi({ ...li, password: e.target.value })}
                        className="font-mono"
                        required
                      />
                    </div>
                    {error && (
                      <Alert variant="destructive" data-testid="login-error">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>{error}</AlertDescription>
                      </Alert>
                    )}
                    <Button
                      data-testid="login-submit-btn"
                      type="submit"
                      className="w-full tracking-widest font-mono"
                      disabled={loading}
                    >
                      {loading ? "AUTHENTICATING..." : "ENTER CONSOLE →"}
                    </Button>

                  </form>
                </TabsContent>

                <TabsContent value="register">
                  <form onSubmit={onRegister} className="space-y-4">
                    <div>
                      <Label htmlFor="reg-name">Full Name</Label>
                      <Input
                        id="reg-name"
                        data-testid="register-name-input"
                        value={ri.name}
                        onChange={(e) => setRi({ ...ri, name: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="reg-agency">Agency</Label>
                      <Input
                        id="reg-agency"
                        data-testid="register-agency-input"
                        value={ri.agency}
                        onChange={(e) => setRi({ ...ri, agency: e.target.value })}
                        placeholder="e.g. County Fire & Rescue"
                      />
                    </div>
                    <div>
                      <Label htmlFor="reg-state">Home State (2-letter abbreviation)</Label>
                      <Input
                        id="reg-state"
                        data-testid="register-state-input"
                        value={ri.address_state}
                        onChange={(e) => setRi({ ...ri, address_state: e.target.value.toUpperCase() })}
                        placeholder="NJ"
                        maxLength={2}
                        className="font-mono uppercase"
                        required
                      />
                      <p className="text-[10px] text-muted-foreground mt-1">Used to generate your dispatcher username (e.g. 1001NJ)</p>
                    </div>
                    <div>
                      <Label htmlFor="reg-email">Email</Label>
                      <Input
                        id="reg-email"
                        data-testid="register-email-input"
                        type="email"
                        value={ri.email}
                        onChange={(e) => setRi({ ...ri, email: e.target.value })}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="reg-password">Passcode</Label>
                      <Input
                        id="reg-password"
                        data-testid="register-password-input"
                        type="password"
                        value={ri.password}
                        onChange={(e) => setRi({ ...ri, password: e.target.value })}
                        minLength={6}
                        required
                      />
                    </div>
                    {error && (
                      <Alert variant="destructive">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>{error}</AlertDescription>
                      </Alert>
                    )}
                    <Button
                      data-testid="register-submit-btn"
                      type="submit"
                      className="w-full tracking-widest font-mono"
                      disabled={loading}
                    >
                      {loading ? "CREATING ACCOUNT..." : "REQUEST ACCESS →"}
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
